import { Button, Group, Text, Tooltip } from '@mantine/core';
import type { ReactNode } from 'react';

interface Props {
  label: ReactNode;
  required?: boolean;
  /** Текст кнопки справа. */
  actionLabel: ReactNode;
  actionIcon?: ReactNode;
  onAction: () => void;
  /** Кнопка disabled (например, если справочник пуст). */
  disabled?: boolean;
  /** Всплывающая подсказка при наведении на название поля (вместо description). */
  hint?: ReactNode;
}

/**
 * Замена `label` у `TextInput`/`Textarea`: слева — название поля,
 * справа — кнопка «Из …». Используется чтобы заменить громоздкие
 * Select-выпадашки на компактные пикеры рядом с лейблами.
 *
 * Применение:
 * ```
 * <TextInput
 *   label={<LabelWithAction label="Описание груза" actionLabel="Из «Мои грузы»" onAction={...} />}
 *   ...
 * />
 * ```
 */
export function LabelWithAction({
  label,
  required,
  actionLabel,
  actionIcon,
  onAction,
  disabled,
  hint,
}: Props) {
  const labelText = (
    <Text
      size="sm"
      fw={500}
      component="span"
      style={hint ? { borderBottom: '1px dotted var(--mantine-color-dimmed)', cursor: 'help' } : undefined}
    >
      {label}
    </Text>
  );
  return (
    <Group justify="space-between" wrap="nowrap" gap="xs" style={{ width: '100%' }}>
      <Text size="sm" fw={500} component="span">
        {hint ? (
          <Tooltip label={hint} withArrow multiline w={260} position="top-start">
            {labelText}
          </Tooltip>
        ) : (
          label
        )}
        {required && (
          <Text component="span" c="red" ml={4}>
            *
          </Text>
        )}
      </Text>
      <Button
        size="compact-xs"
        variant="subtle"
        leftSection={actionIcon}
        onClick={onAction}
        disabled={disabled}
        // Не отправлять форму при клике.
        type="button"
      >
        {actionLabel}
      </Button>
    </Group>
  );
}
